/**
 * Listings API client
 *
 * @fileoverview Typed API helpers for listing list and update operations used by AP-73.
 *
 * @changeHistory
 * - 2026-05-04: Initial creation - Added listings list/update client with response normalization (GitHub Copilot)
 * - 2026-05-09: Expanded commercial listing contracts for new PDA fields (GitHub Copilot)
 * - 2026-07-07: Added shared listing detail query options for breadcrumb labels (Codex)
 * - 2026-08-14: Added Realtor residential source, category, and override controls (Codex)
 * - 2026-08-17: Added PDA gallery variants and current Realtor metadata (Codex)
 * - 2026-08-18: Added the Realtor listing date to the client listing contract (Copilot)
 * - 2026-08-24: Allowed partial Realtor metadata for cache-miss projections (Codex)
 * - 2026-09-30: Allowed explicit commercial field clears for AP-110 (Copilot)
 */

import {
  queryOptions,
  type DataTag,
  type UndefinedInitialDataOptions,
} from '@tanstack/react-query';
import { useAuthStore } from '../shared/stores/auth.store';
import { API_BASE_URL, ApiError, apiClient } from './api-client';

export type ListingType = 'residential' | 'commercial';
export type PortalStatus = 'draft' | 'active' | 'archived';
export type ListingCategory = 'sale' | 'rent';
export type ResidentialPropertySubtype =
  | 'single-family'
  | 'condo'
  | 'townhome'
  | 'multi-family'
  | 'land';

export type CommercialPropertyType =
  | 'office'
  | 'retail'
  | 'industrial'
  | 'land'
  | 'mixed-use'
  | 'flex'
  | 'medical'
  | 'r-and-d'
  | 'warehouse'
  | 'multifamily';
export type CommercialLeaseType = 'sale' | 'lease' | 'both';
export type TenancyType = 'single' | 'multi';

export interface ListingImage {
  url: string;
  caption?: string;
  altText?: string;
  order?: number;
  isCover?: boolean;
}

export interface CommercialListingAgent {
  name?: string;
  title?: string;
  phone?: string;
  email?: string;
  imageUrl?: string;
}

export interface CommercialListingDemographics {
  population1Mile?: number;
  population3Mile?: number;
  medianIncome1Mile?: number;
  medianIncome3Mile?: number;
  trafficCount?: number;
}

export interface CommercialListingFloorPlan {
  name?: string;
  sizeLabel?: string;
  available?: boolean;
}

export interface ResidentialListingFormValues {
  agentId?: string;
  mlsId?: string;
  address: string;
  city?: string;
  state?: string;
  zip?: string;
  propertySubtype?: ResidentialPropertySubtype;
  category?: ListingCategory;
  price?: number;
  beds?: number;
  baths?: number;
  sqft?: number;
  yearBuilt?: number;
  description?: string;
  coverImageUrl?: string;
  mlsImageUrl?: string;
  galleryImageUrls?: string[];
  galleryImages?: ListingImage[];
}

export interface CommercialListingFormValues {
  agentId?: string;
  mlsId?: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  status?: string;
  commercialType: CommercialPropertyType;
  leaseType: CommercialLeaseType;
  priceOrRate: number;
  askingPrice?: number;
  pricePerSqft?: number;
  commercialSubtype?: string;
  totalSqft?: number;
  availableSqft?: number;
  lotSizeSqft?: number;
  lotSizeAcres?: number;
  yearBuilt?: number;
  buildingClass?: string;
  occupancyPercent?: number;
  annualExpenses?: number;
  cashOnCashReturn?: number;
  floors?: number;
  yearRenovated?: number;
  parkingType?: string;
  majorTenants?: string[];
  features?: string[];
  demographics?: CommercialListingDemographics;
  floorPlans?: CommercialListingFloorPlan[];
  tenancy?: TenancyType;
  dscr?: number;
  roi?: number;
  capRate?: number;
  noi?: number;
  parkingSpaces?: number;
  parkingRatio?: string;
  zoning?: string;
  landUse?: string;
  parking?: string;
  description?: string;
  coverImageUrl?: string;
  mlsImageUrl?: string;
  galleryImageUrls?: string[];
  galleryImages?: ListingImage[];
  agent?: CommercialListingAgent;
}

export type CreateResidentialListingPayload = ResidentialListingFormValues & {
  portalStatus?: PortalStatus;
  agentId?: string;
};

export type UpdateResidentialListingPayload = {
  [Key in keyof ResidentialListingFormValues]?: ResidentialListingFormValues[Key] | null;
} & {
  portalStatus?: PortalStatus;
  featured?: boolean;
  publicationMode?: 'automatic' | 'manual';
  resetOverrides?: string[];
};

export type CreateCommercialListingPayload = CommercialListingFormValues & {
  portalStatus?: PortalStatus;
  agentId?: string | null;
};

export type UpdateCommercialListingPayload = {
  [Key in keyof Omit<CommercialListingFormValues, 'mlsId'>]?:
    | Omit<CommercialListingFormValues, 'mlsId'>[Key]
    | null;
} & {
  portalStatus?: PortalStatus;
  featured?: boolean;
};

export interface ListingRecord {
  id: string;
  type?: ListingType;
  address?: string;
  portalStatus: PortalStatus;
  status?: string;
  featured?: boolean;
  agentId?: string;
  agentName?: string;
  createdByUserId?: string;
  mlsStatus?: string | null;
  category?: ListingCategory;
  source?: 'manual' | 'realtor';
  publicationMode?: 'automatic' | 'manual';
  overrideFields?: string[];
  realtor?: {
    propertyId?: string;
    officeFulfillmentId?: number;
    status?: string;
    mlsStatus?: string | null;
    listDate?: string | null;
    lastSyncedAt?: string;
    sourceAgentId?: string | null;
    sourceCategory?: ListingCategory | null;
  };
  createdAt?: string;
  coverImageUrl?: string | null;
  mlsImageUrl?: string | null;
  galleryThumbnailUrls?: string[];
  galleryImageUrls?: string[];
  galleryWebpUrls?: string[];
  galleryImages?: ListingImage[];
  commercialType?: CommercialPropertyType;
  leaseType?: CommercialLeaseType;
  priceOrRate?: number;
  askingPrice?: number;
  pricePerSqft?: number;
  commercialSubtype?: string;
  totalSqft?: number;
  availableSqft?: number;
  lotSizeSqft?: number;
  lotSizeAcres?: number;
  yearBuilt?: number;
  buildingClass?: string;
  occupancyPercent?: number;
  annualExpenses?: number;
  cashOnCashReturn?: number;
  floors?: number;
  yearRenovated?: number;
  parkingType?: string;
  majorTenants?: string[];
  features?: string[];
  demographics?: CommercialListingDemographics;
  floorPlans?: CommercialListingFloorPlan[];
  capRate?: number;
  agent?: CommercialListingAgent;
  [key: string]: unknown;
}

export interface ListingImageOperationResult {
  listing: ListingRecord;
  images: ListingImage[];
  fallbackMode: boolean;
  message?: string;
}

export interface ListingsListParams {
  portalStatus?: PortalStatus;
  type?: ListingType;
  limit?: number;
  offset?: number;
  agentId?: string;
}

export interface ListingsPagination {
  total: number;
  page: number;
  limit: number;
  pages: number;
  hasMore: boolean;
}

export interface ListingsListResult {
  items: ListingRecord[];
  pagination: ListingsPagination;
}

type PaginationPayload = {
  total?: number;
  page?: number;
  limit?: number;
  offset?: number;
  pages?: number;
  totalPages?: number;
  hasMore?: boolean;
};

type PaginatedEnvelope = {
  data?: ListingRecord[];
  items?: ListingRecord[];
  listings?: ListingRecord[];
  pagination?: PaginationPayload;
  meta?: PaginationPayload;
};

type ListResponse = {
  status: 'success';
  data: ListingRecord[] | PaginatedEnvelope;
  pagination?: PaginationPayload;
  meta?: PaginationPayload;
  message?: string;
};

type UpdateResponse = {
  status: 'success';
  data: ListingRecord;
  message?: string;
};

type CreateResponse = {
  status: 'success';
  data: ListingRecord;
  message?: string;
};

type GetByIdResponse = {
  status: 'success';
  data: ListingRecord;
  message?: string;
};

type ListingImagesResponse = {
  status: 'success';
  data: ListingImageOperationResult;
  message?: string;
};

const toNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return null;
};

const isListingArray = (value: unknown): value is ListingRecord[] => Array.isArray(value);

const normalizeListResponse = (
  response: ListResponse,
  params: ListingsListParams
): ListingsListResult => {
  const fallbackLimit = params.limit ?? 25;
  const fallbackOffset = params.offset ?? 0;

  if (isListingArray(response.data)) {
    const paginationSource = response.pagination ?? response.meta;
    const normalizedLimit = toNumber(paginationSource?.limit) ?? fallbackLimit;
    const normalizedOffset = toNumber(paginationSource?.offset) ?? fallbackOffset;
    const normalizedTotal =
      toNumber(paginationSource?.total) ??
      Math.max(response.data.length, response.data.length + normalizedOffset);
    const normalizedPage =
      toNumber(paginationSource?.page) ??
      Math.floor(normalizedOffset / Math.max(normalizedLimit, 1)) + 1;
    const normalizedPages =
      toNumber(paginationSource?.pages) ??
      toNumber(paginationSource?.totalPages) ??
      Math.max(1, Math.ceil(normalizedTotal / Math.max(normalizedLimit, 1)));
    const normalizedHasMore =
      typeof paginationSource?.hasMore === 'boolean'
        ? paginationSource.hasMore
        : normalizedPage < normalizedPages;

    return {
      items: response.data,
      pagination: {
        total: normalizedTotal,
        page: normalizedPage,
        limit: normalizedLimit,
        pages: normalizedPages,
        hasMore: normalizedHasMore,
      },
    };
  }

  const envelope = response.data;
  const items = envelope.data ?? envelope.items ?? envelope.listings ?? [];
  const paginationSource =
    response.pagination ?? response.meta ?? envelope.pagination ?? envelope.meta;
  const normalizedOffset = toNumber(paginationSource?.offset) ?? fallbackOffset;

  const normalizedLimit = toNumber(paginationSource?.limit) ?? fallbackLimit;
  const normalizedPage =
    toNumber(paginationSource?.page) ??
    Math.floor(normalizedOffset / Math.max(normalizedLimit, 1)) + 1;
  const normalizedTotal =
    toNumber(paginationSource?.total) ?? Math.max(items.length, items.length + normalizedOffset);
  const normalizedPages =
    toNumber(paginationSource?.pages) ??
    toNumber(paginationSource?.totalPages) ??
    Math.max(1, Math.ceil(normalizedTotal / Math.max(normalizedLimit, 1)));

  const normalizedHasMore =
    typeof paginationSource?.hasMore === 'boolean'
      ? paginationSource.hasMore
      : normalizedPage < normalizedPages;

  return {
    items,
    pagination: {
      total: normalizedTotal,
      page: normalizedPage,
      limit: normalizedLimit,
      pages: normalizedPages,
      hasMore: normalizedHasMore,
    },
  };
};

export const listingsApi = {
  async list(params: ListingsListParams = {}): Promise<ListingsListResult> {
    const query = new URLSearchParams();

    if (params.portalStatus) {
      query.set('portalStatus', params.portalStatus);
    }

    if (params.type) {
      query.set('type', params.type);
    }

    if (typeof params.limit === 'number') {
      query.set('limit', String(params.limit));
    }

    if (typeof params.offset === 'number') {
      query.set('offset', String(params.offset));
    }

    if (params.agentId) {
      query.set('agentId', params.agentId);
    }

    const queryString = query.toString();
    const endpoint = `/listings${queryString ? `?${queryString}` : ''}`;
    const response = await apiClient.get<ListResponse>(endpoint);

    return normalizeListResponse(response, params);
  },

  async update(id: string, patch: UpdateResidentialListingPayload): Promise<ListingRecord> {
    const response = await apiClient.patch<UpdateResponse>(`/listings/${id}`, patch);
    return response.data;
  },

  async create(payload: CreateResidentialListingPayload): Promise<ListingRecord> {
    const response = await apiClient.post<CreateResponse>('/listings', payload);
    return response.data;
  },

  async getById(id: string): Promise<ListingRecord> {
    const response = await apiClient.get<GetByIdResponse>(`/listings/${id}`);
    return response.data;
  },

  async createCommercial(payload: CreateCommercialListingPayload): Promise<ListingRecord> {
    const response = await apiClient.post<CreateResponse>('/listings/commercial', payload);
    return response.data;
  },

  async getCommercialById(id: string): Promise<ListingRecord> {
    const response = await apiClient.get<GetByIdResponse>(`/listings/commercial/${id}`);
    return response.data;
  },

  async updateCommercial(
    id: string,
    patch: UpdateCommercialListingPayload
  ): Promise<ListingRecord> {
    const response = await apiClient.patch<UpdateResponse>(`/listings/commercial/${id}`, patch);
    return response.data;
  },

  async listImages(id: string): Promise<ListingImageOperationResult> {
    const response = await apiClient.get<ListingImagesResponse>(`/listings/${id}/images`);
    return response.data;
  },

  async uploadImages(id: string, files: File[]): Promise<ListingImageOperationResult> {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('images', file);
    });

    const { accessToken } = useAuthStore.getState();
    const response = await fetch(`${API_BASE_URL}/listings/${id}/images`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
      headers: accessToken
        ? {
            Authorization: `Bearer ${accessToken}`,
          }
        : undefined,
    });

    if (!response.ok) {
      let message = 'Failed to upload listing images';

      try {
        const error = (await response.json()) as { message?: string };
        if (typeof error?.message === 'string' && error.message.length > 0) {
          message = error.message;
        }
      } catch {
        // Ignore non-JSON error bodies and keep fallback message.
      }

      throw new Error(message);
    }

    const result = (await response.json()) as ListingImagesResponse;
    return result.data;
  },

  /**
   * Upload files to storage and get back the resulting public URLs.
   *
   * Use this when you want to add images to a listing without immediately
   * patching its rich `galleryImages`. The returned URLs are intended to be
   * appended to `galleryImageUrls` and persisted via the regular listing
   * create/update payload.
   */
  async uploadImagesToStorage(files: File[]): Promise<string[]> {
    if (files.length === 0) {
      return [];
    }

    const formData = new FormData();
    files.forEach((file) => {
      formData.append('images', file);
    });

    const { accessToken } = useAuthStore.getState();
    const response = await fetch(`${API_BASE_URL}/listings/storage/images`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
      headers: accessToken
        ? {
            Authorization: `Bearer ${accessToken}`,
          }
        : undefined,
    });

    if (!response.ok) {
      let message = 'Failed to upload images';

      try {
        const error = (await response.json()) as { message?: string };
        if (typeof error?.message === 'string' && error.message.length > 0) {
          message = error.message;
        }
      } catch {
        // Ignore non-JSON error bodies and keep fallback message.
      }

      throw new Error(message);
    }

    const result = (await response.json()) as { data: { urls: string[] } };
    return result.data.urls;
  },

  async updateImages(id: string, images: ListingImage[]): Promise<ListingImageOperationResult> {
    const response = await apiClient.patch<ListingImagesResponse>(`/listings/${id}/images`, {
      images,
    });

    return response.data;
  },

  async deleteImage(id: string, imageUrl: string): Promise<ListingImageOperationResult> {
    const response = await apiClient.delete<ListingImagesResponse>(`/listings/${id}/images`, {
      body: JSON.stringify({
        imageUrl,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return response.data;
  },
};

type ListingDetailsQueryKey = readonly ['listing-details', string];
type ListingDetailsQueryOptions = UndefinedInitialDataOptions<
  ListingRecord,
  ApiError,
  ListingRecord,
  ListingDetailsQueryKey
> & {
  queryKey: DataTag<ListingDetailsQueryKey, ListingRecord, ApiError>;
};

type ResidentialListingQueryKey = readonly ['listing', string];
type ResidentialListingQueryOptions = UndefinedInitialDataOptions<
  ListingRecord,
  ApiError,
  ListingRecord,
  ResidentialListingQueryKey
> & {
  queryKey: DataTag<ResidentialListingQueryKey, ListingRecord, ApiError>;
};

type CommercialListingQueryKey = readonly ['commercial-listing', string];
type CommercialListingQueryOptions = UndefinedInitialDataOptions<
  ListingRecord,
  ApiError,
  ListingRecord,
  CommercialListingQueryKey
> & {
  queryKey: DataTag<CommercialListingQueryKey, ListingRecord, ApiError>;
};

export const getListingDetailsQueryOptions = (id: string): ListingDetailsQueryOptions =>
  queryOptions<ListingRecord, ApiError, ListingRecord, ListingDetailsQueryKey>({
    queryKey: ['listing-details', id],
    queryFn: async (): Promise<ListingRecord> => {
      try {
        return await listingsApi.getById(id);
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) {
          return listingsApi.getCommercialById(id);
        }

        throw error;
      }
    },
  });

export const getResidentialListingQueryOptions = (id: string): ResidentialListingQueryOptions =>
  queryOptions<ListingRecord, ApiError, ListingRecord, ResidentialListingQueryKey>({
    queryKey: ['listing', id],
    queryFn: async () => listingsApi.getById(id),
  });

export const getCommercialListingQueryOptions = (id: string): CommercialListingQueryOptions =>
  queryOptions<ListingRecord, ApiError, ListingRecord, CommercialListingQueryKey>({
    queryKey: ['commercial-listing', id],
    queryFn: async () => listingsApi.getCommercialById(id),
  });
